import { PaymentMethodAvailable } from "@/@types/User";
import { useAuth } from "@/auth/Auth";
import CustomSelect from "@/components/shared/CustomSelect/CustomSelect";
import { MultiComboBox } from "@/components/shared/MultiComboBox/MultiComboBox";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Info } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  resolveFeesSelectedMethods,
  saveFeesSelectedMethods,
} from "../utils/feesSelectedMethodsStorage";

interface FeeItem {
  id: number;
  paymentMethodId: number;
  percentage: string;
  installments: number;
  fixed: string;
  fixed_fee: string;
  percentage_fee: string;
  type: string;
  type_label: string;
}

interface PaymentMethod {
  payment_method_type_label: string;
  payment_method_type: string;
  payment_method_category_name?: string;
  fees: FeeItem[];
  retention?: {
    retention_days: number;
    retention_percentage: string;
    retention_percentage_formatted: string;
  };
  payout?: {
    payout_days: number;
  };
}

interface ReservedItem {
  payment_method_type_label: string;
  payment_method_type: string;
  retention_in_days: number;
  retention_percentage: number;
  retention_percentage_formatted: string;
}

interface PayoutItem {
  payment_method_type_label: string;
  payment_method_type: string;
  payout_days: number;
}

interface FeesTabProps {
  taxes: PaymentMethod[];
  reserved: Record<string, ReservedItem | undefined> | ReservedItem[] | undefined;
  payouts: Record<string, PayoutItem | undefined> | PayoutItem[] | undefined;
  withdrawal_fee:
    | {
        percentage_fee: string;
        fixed_fee: number;
        percentage_fee_formatted: string;
        fixed_fee_formatted: string;
      }
    | undefined;
  isLoading?: boolean;
  feeType: string;
  onFeeTypeChange: (type: string) => void;
}

function categoryOrderFromAvailable(methods: PaymentMethodAvailable[]): string[] {
  return Array.from(
    new Set(
      methods
        .map((method) => method.payment_method_type)
        .filter((slug): slug is string => Boolean(slug)),
    ),
  );
}

function categorySlugByType(methods: PaymentMethodAvailable[]): Map<string, string> {
  const map = new Map<string, string>();

  for (const method of methods) {
    if (method.payment_method_type) {
      map.set(method.type, method.payment_method_type);
    }
  }

  return map;
}

function categoryRank(slug: string | undefined, order: string[]): number {
  if (!slug) return order.length;
  const index = order.indexOf(slug);
  return index === -1 ? order.length : index;
}

function asKeyedMap<T extends { payment_method_type: string }>(
  value: Record<string, T | undefined> | T[] | undefined,
): Record<string, T | undefined> | undefined {
  if (!value) return undefined;
  if (!Array.isArray(value)) return value;

  return Object.fromEntries(value.map((item) => [item.payment_method_type, item]));
}

export function FeesTab({
  taxes,
  reserved,
  payouts,
  withdrawal_fee,
  isLoading,
  feeType,
  onFeeTypeChange,
}: FeesTabProps) {
  const { t } = useTranslation("wallet");
  const { authData } = useAuth();
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const availablePaymentMethods = authData?.available_payment_methods;

  const feesTypes = [
    { value: "national", label: "Nacional (BR)" },
    { value: "international", label: "Internacional" },
  ];

  const payoutsMap = asKeyedMap(payouts);
  const reservedMap = asKeyedMap(reserved);

  const availableTypes = useMemo(
    () => taxes.map((method) => method.payment_method_type),
    [taxes],
  );
  const availableKey = availableTypes.join(",");

  useEffect(() => {
    const available = availableKey ? availableKey.split(",") : [];
    setSelectedMethods(resolveFeesSelectedMethods(feeType, available));
  }, [feeType, availableKey]);

  const selectedTaxes = useMemo(
    () =>
      selectedMethods
        .map((key) => taxes.find((method) => method.payment_method_type === key))
        .filter((method): method is PaymentMethod => Boolean(method)),
    [selectedMethods, taxes],
  );

  const fetchMethodOptions = useCallback(
    async (query: string) => {
      const term = query.trim().toLowerCase();
      const methods = availablePaymentMethods ?? [];
      const categoryOrder = categoryOrderFromAvailable(methods);
      const slugByType = categorySlugByType(methods);

      return [...taxes]
        .sort((a, b) => {
          const byCategory =
            categoryRank(slugByType.get(a.payment_method_type), categoryOrder) -
            categoryRank(slugByType.get(b.payment_method_type), categoryOrder);
          if (byCategory !== 0) return byCategory;

          return a.payment_method_type_label.localeCompare(
            b.payment_method_type_label,
          );
        })
        .filter((method) =>
          method.payment_method_type_label.toLowerCase().includes(term),
        )
        .map((method) => ({
          value: method.payment_method_type,
          label: method.payment_method_type_label,
          group:
            method.payment_method_category_name ||
            slugByType.get(method.payment_method_type) ||
            "Outros",
        }));
    },
    [taxes, availablePaymentMethods],
  );

  const handleMethodsChange = (values: string[]) => {
    const next = Array.isArray(values) ? values.filter(Boolean) : [];
    setSelectedMethods(next);
    saveFeesSelectedMethods(feeType, next);
  };

  const getDefaultFee = (method: PaymentMethod) => {
    return (
      method?.fees?.find((fee) => fee.installments === 1) || method?.fees?.[0]
    );
  };

  const formatFees = (percentage: string, fixed: string) => {
    const cleanPercentage = percentage.trim();
    const cleanFixed = fixed.trim();

    const isValidPercentage =
      cleanPercentage !== "" &&
      cleanPercentage !== "0,00%" &&
      cleanPercentage !== "%";

    const isValidFixed =
      cleanFixed !== "" && !cleanFixed.match(/R\$\s?0,00|US\$\s?0,00/);

    if (isValidPercentage && isValidFixed) {
      return `${percentage} + ${fixed} ${t("byApprovedSale")}`;
    }
    if (isValidPercentage) {
      return `${percentage} ${t("byApprovedSale")}`;
    }
    if (isValidFixed) {
      return `${fixed} ${t("byApprovedSale")}`;
    }
    return t("noFees");
  };

  const formatDays = (days: number) => {
    if (days === 0) return t("instant");
    if (days === 1) return t("oneDay");
    return t("days_count", { count: days });
  };

  const formatWithdrawalFee = () => {
    if (!withdrawal_fee) return t("noFees");
    const hasPercentage = withdrawal_fee.percentage_fee !== "0.00";
    const hasFixed = withdrawal_fee.fixed_fee !== 0;

    if (hasPercentage && hasFixed) {
      return `${withdrawal_fee.percentage_fee_formatted} + ${
        withdrawal_fee.fixed_fee_formatted
      } ${t("perWithdrawal")}`;
    }
    if (hasPercentage) {
      return `${withdrawal_fee.percentage_fee_formatted} ${t("perWithdrawal")}`;
    }
    if (hasFixed) {
      return `${withdrawal_fee.fixed_fee_formatted} ${t("perWithdrawal")}`;
    }
    return t("noFees");
  };

  const getPayoutDays = (method: PaymentMethod) => {
    return method.payout?.payout_days ?? payoutsMap?.[method.payment_method_type]?.payout_days;
  };

  const getReserveInfo = (method: PaymentMethod) => {
    const reserve = reservedMap?.[method.payment_method_type];
    const days = method.retention?.retention_days ?? reserve?.retention_in_days;
    const percentage =
      method.retention?.retention_percentage_formatted ??
      reserve?.retention_percentage_formatted;

    if (days === undefined || percentage === undefined) return null;
    return { days, percentage };
  };

  return (
    <div className="flex gap-6 max-lg:flex-col">
      <div className="max-w-xs w-full space-y-1">
        <h1 className="text-base font-semibold">{t("feesAndDeadlines")}</h1>
        <p className="text-muted-foreground text-sm">{t("feesDescription")}</p>
      </div>

      {isLoading ? (
        <div className="border rounded-xl w-full max-w-4xl p-6">
          <div className="space-y-8 text-sm">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-4">
                <Skeleton className="h-12" />
                <Skeleton className="h-6" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="border rounded-xl w-full max-w-4xl p-6">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <h2 className="text-sm font-semibold whitespace-nowrap mr-1">
              {t("yourFees")}
            </h2>
            <div className="w-[190px] shrink-0">
              <CustomSelect
                options={feesTypes}
                placeholder=""
                value={feeType}
                onValueChange={onFeeTypeChange}
              />
            </div>
            <div className="w-[220px] shrink-0 [&>div]:gap-0 [&_button]:h-10 [&_button]:min-h-10">
              <MultiComboBox
                fetchOptions={fetchMethodOptions}
                values={selectedMethods}
                onChange={handleMethodsChange}
                placeholder={t("paymentMethodPlaceholder")}
                queryKey={`fees-methods-${feeType}`}
                newParams={[feeType, ...availableTypes]}
                isEditing
                maxHeight={320}
                formatTriggerLabel={(selected) => {
                  if (selected.length === 0) return "";
                  if (selected.length === 1) return selected[0].label || "";
                  return t("methodsSelectedCount", { count: selected.length });
                }}
              />
            </div>
          </div>

          <div className="text-sm">
            {selectedTaxes.length === 0 ? (
              <p className="text-muted-foreground mb-[18px]">
                {t("selectAtLeastOneMethod")}
              </p>
            ) : (
              <div className="space-y-3.5 mb-[18px]">
                {selectedTaxes.map((method) => (
                  <div key={`fee-${method.payment_method_type}`}>
                    <p className="text-muted-foreground text-xs mb-0.5">
                      {method.payment_method_type_label}:
                    </p>
                    <p className="text-[13px] font-semibold">
                      {formatFees(
                        getDefaultFee(method)?.percentage || "",
                        getDefaultFee(method)?.fixed || "",
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mb-[18px]">
              <p className="text-muted-foreground text-xs mb-0.5">{t("withdrawal")}:</p>
              <p className="text-[13px] font-semibold">{formatWithdrawalFee()}</p>
            </div>

            <Separator className="my-[18px]" />

            <h2 className="text-[13px] font-semibold mb-3.5">{t("receiptDeadlines")}:</h2>
            <div className="space-y-3.5">
              {selectedTaxes.map((method) => {
                const days = getPayoutDays(method);
                if (days === undefined || days === null) return null;

                return (
                  <div key={`payout-${method.payment_method_type}`}>
                    <p className="text-muted-foreground text-xs mb-0.5">
                      {method.payment_method_type_label}:
                    </p>
                    <p className="text-[13px] font-semibold">{formatDays(days)}</p>
                  </div>
                );
              })}
            </div>

            <Separator className="my-[18px]" />

            <h2 className="text-[13px] font-semibold mb-3.5">{t("securityReserve")}</h2>
            <div className="space-y-3.5">
              {selectedTaxes.map((method) => {
                const reserve = getReserveInfo(method);
                if (!reserve) return null;

                return (
                  <div
                    key={`reserve-${method.payment_method_type}`}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5"
                  >
                    <div>
                      <p className="text-muted-foreground text-xs mb-0.5">
                        {method.payment_method_type_label} — {t("retentionPercentage")}
                      </p>
                      <p className="text-[13px] font-semibold">
                        {reserve.days === 0 ? "0%" : reserve.percentage}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-0.5">
                        {method.payment_method_type_label} — {t("retentionDays")}
                      </p>
                      <p className="text-[13px] font-semibold">
                        {reserve.days === 0 ? "0" : reserve.days}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 bg-zinc-900 rounded-lg p-3.5 flex gap-2.5 items-start">
              <Info
                size={14}
                className="text-muted-foreground flex-shrink-0 mt-0.5"
              />
              <p className="text-muted-foreground text-xs leading-relaxed">
                {t("reserveDescription")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
